import React, { useState } from 'react'
import {Redirect, useParams} from 'react-router-dom'
import PropTypes from 'prop-types'
import {File} from "@peracto/peracto-ui";
import { useFormikContext } from 'formik';

import {
    Form,
    Group,
    Input,
    Modal,
    FormActions,
    Checkbox,
    Textarea,
} from '@peracto/peracto-ui'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'

import { toast } from 'react-toastify'

import { DELETE, useClient } from '@peracto/client'

export const MODE_ADD = 'add'
export const MODE_EDIT = 'edit'

const FormContent = () => {
    const { values } = useFormikContext();

    return (
        <Group key="core" id="core" name="Core">
            <div data-testid="product-option-details-section">
                <File
                    label="Configurator Option Image"
                    name="location"
                    placeholder="Assign an image to product option detail"
                    testId="location"
                />

                <div className="form-group">
                    <div className="d-flex justify-content-between align-items-center">
                        <label className="mb-1 fw-bold">Configurator Image Preview</label>
                    </div>
                    <div className="input-group p-3 border rounded bg-light justify-content-center">
                        <img
                            src={values.location || '/images/placeholder.png'}
                            alt="Product Option Detail Preview"
                            style={{ maxHeight: '200px', width: 'auto' }}
                        />
                    </div>
                </div>

                <Input
                    name="variantOptionIdentifier"
                    label="Variant Option Identifier"
                    testId="variantOptionIdentifier"
                    placeholder="Enter Variant Option Identifier..."
                    required
                />

                <Textarea
                    name="description"
                    label="Description"
                    placeholder="Enter an optional description..."
                    testId="description"
                />

                <Checkbox name="expressDelivery" label="Express Delivery (For Blind Colour Configurators)"
                          testId="expressDelivery"/>
                <Checkbox name="bestSeller" label="Best Seller (For Blind Colour Configurators)"
                          testId="bestSeller"/>
            </div>
        </Group>
    )
}

const ProductOptionDetailsForm = ({ mode = MODE_EDIT, ...props }) => {
    const [showDialog, setShowDialog] = useState(false)
    const [redirect, setRedirect] = useState()
    const { id } = useParams();

    const { client } = useClient()

    const onDelete = async () => {
        try {
            await client(DELETE, 'product-option-details', {
                id: props.values.id,
            })

            toast.success('Product option detail deleted successfully!')
            setRedirect(`/options/${id}/details`)
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }

    return (
        <>
            {redirect && <Redirect to={redirect} />}

            {mode === MODE_EDIT && (
                <FormActions>
                    <button
                        className="text-danger"
                        onClick={() => setShowDialog(true)}
                        data-testid="delete-product-option-detail"
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        Delete Product Option Detail
                    </button>
                </FormActions>
            )}

            <Form
                autoComplete="off"
                {...props}
                values={{
                    productOption: `/product-options/${id}`,
                    variantOptionIdentifier: '',
                    location: null,
                    description: null,
                    expressDelivery: false,
                    bestSeller: false,
                    ...props.values,
                }}
            >
                <FormContent />
            </Form>

            {mode === MODE_EDIT && (
                <Modal
                    isVisible={showDialog}
                    title="Delete Product Option Detail"
                    close={() => setShowDialog(false)}
                    buttons={[
                        {
                            type: 'btn-outline-secondary',
                            text: 'Close',
                            action: () => setShowDialog(false),
                        },
                        {
                            type: 'btn-danger',
                            text: 'Delete Product Option Detail',
                            action: () => onDelete(),
                        },
                    ]}
                >
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        size="4x"
                        className="d-block mb-4"
                    />
                    Are you sure you would like to permanently delete this product option detail? Deleted product option details
                    cannot be recovered.
                </Modal>
            )}
        </>
    )
}

ProductOptionDetailsForm.displayName = 'ProductOptionDetailsForm'

ProductOptionDetailsForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_ADD, MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default ProductOptionDetailsForm
